import React, { forwardRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { AlertCircle } from 'lucide-react';

export interface TextareaProps
    extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    error?: string;
    label?: string;
    helperText?: string;
    containerClassName?: string;
    variant?: 'default' | 'filled' | 'ghost' | 'error';
    textareaSize?: 'sm' | 'default' | 'lg';
    resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
        {
            className,
            error,
            label,
            helperText,
            containerClassName,
            variant = 'default',
            textareaSize = 'default',
            resize = 'vertical',
            disabled,
            rows = 4,
            ...props
        },
        ref
    ) => {
        const [isFocused, setIsFocused] = useState(false);
        const hasError = !!error;

        // Variant styles - using ThreatScope color system
        const variantStyles = {
            default: cn(
                "bg-background-light-secondary dark:bg-background-dark-secondary",
                "border border-border-light dark:border-border-dark-subtle",
                "focus:bg-background-light dark:focus:bg-background-dark-elevated",
                "focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                "hover:border-border-light dark:hover:border-border-dark",
                !hasError && isFocused && "border-primary-500 ring-2 ring-primary-500"
            ),
            filled: cn(
                "bg-background-light-elevated dark:bg-background-dark-elevated",
                "border border-transparent",
                "focus:bg-background-light dark:focus:bg-background-dark-secondary",
                "focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                "hover:bg-background-light-secondary dark:hover:bg-background-dark-secondary"
            ),
            ghost: cn(
                "bg-transparent",
                "border border-border-light dark:border-border-dark-subtle",
                "focus:bg-background-light-secondary dark:focus:bg-background-dark-secondary",
                "focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                "hover:bg-background-light-secondary dark:hover:bg-background-dark-secondary"
            ),
            error: cn(
                "bg-background-light-secondary dark:bg-background-dark-secondary",
                "border border-red-300 dark:border-red-700",
                "focus:bg-background-light dark:focus:bg-background-dark-elevated",
                "focus:ring-2 focus:ring-red-500 focus:border-red-500"
            )
        };

        // Size styles
        const sizeStyles = {
            sm: "px-3 py-2 text-xs",
            default: "px-3 py-2 text-sm",
            lg: "px-4 py-3 text-base"
        };

        // Resize styles
        const resizeStyles = {
            none: "resize-none",
            vertical: "resize-y",
            horizontal: "resize-x",
            both: "resize"
        };

        return (
            <div className={cn("w-full space-y-1", containerClassName)}>
                {label && (
                    <label className="block text-sm font-medium font-grotesk text-text-primary-light dark:text-text-primary-dark">
                        {label}
                    </label>
                )}

                <textarea
                    className={cn(
                        "flex w-full rounded-lg font-roboto text-text-primary-light dark:text-text-primary-dark transition-all duration-200",
                        "placeholder:text-text-muted-light dark:placeholder:text-text-muted-dark",
                        "disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none",
                        "min-h-[2.5rem]",
                        sizeStyles[textareaSize],
                        resizeStyles[resize],
                        variantStyles[hasError ? 'error' : variant],
                        className
                    )}
                    ref={ref}
                    disabled={disabled}
                    rows={rows}
                    onFocus={(e) => {
                        setIsFocused(true);
                        props.onFocus?.(e);
                    }}
                    onBlur={(e) => {
                        setIsFocused(false);
                        props.onBlur?.(e);
                    }}
                    {...props}
                />

                {(error || helperText) && (
                    <div className="flex items-start space-x-1">
                        {error && (
                            <>
                                <AlertCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-red-500 font-roboto">{error}</p>
                            </>
                        )}
                        {!error && helperText && (
                            <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-roboto">{helperText}</p>
                        )}
                    </div>
                )}
            </div>
        );
    }
);

Textarea.displayName = 'Textarea';